import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { errorMessage, get, post } from "../api";
import { Alert, Button, Dialog, Empty, Field, Loading } from "./ui";

type Hit = { id: string; name: string; parentName: string | null; street: string; linkedTo: string | null };

/** "Link to a QuickBooks job": search QuickBooks's customers and jobs and link one to this job (only the CRM changes). */
export function LinkQbJobDialog({ jobId, jobLabel, initial, onClose, onLinked }: { jobId: string; jobLabel: string; initial: string; onClose: () => void; onLinked?: () => void }) {
  const qc = useQueryClient();
  const [q, setQ] = useState(initial);
  const [debounced, setDebounced] = useState(initial);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q), 300);
    return () => clearTimeout(t);
  }, [q]);
  const hits = useQuery({ queryKey: ["qb-customers", debounced], queryFn: () => get<Hit[]>(`/api/integrations/quickbooks/customers?q=${encodeURIComponent(debounced)}`), enabled: debounced.trim().length >= 2 });
  const link = useMutation({
    mutationFn: (qbId: string) => post<{ name: string; customerLinked: boolean }>(`/api/jobs/${jobId}/quickbooks/link`, { qbId }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["job", jobId] });
      await qc.invalidateQueries({ queryKey: ["qb-status"] });
      onLinked?.();
      onClose();
    },
  });
  return (
    <Dialog title={`Link ${jobLabel} to QuickBooks`} onClose={onClose}>
      <div className="flex flex-col gap-3">
        <p className="text-sm text-ink-soft">Find its job (sub-customer) in QuickBooks by name, customer, job number, or street. Only the CRM changes; its customer is linked too when it isn't yet.</p>
        <Field label="Search QuickBooks" value={q} onChange={(e) => setQ(e.target.value)} autoFocus />
        {link.error && <Alert>{errorMessage(link.error)}</Alert>}
        {hits.error && <Alert>{errorMessage(hits.error)}</Alert>}
        {hits.isLoading ? (
          <Loading />
        ) : hits.data && !hits.data.length ? (
          <Empty>Nothing in QuickBooks has that in its name. Try the customer's last name or the street number.</Empty>
        ) : (
          <ul className="flex max-h-96 flex-col overflow-y-auto">
            {hits.data?.map((h) => (
              <li key={h.id} className="flex items-center gap-3 border-b border-mist py-2 last:border-0">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold">{h.name}</div>
                  <div className="text-sm text-muted">{[h.parentName ? `Job under ${h.parentName}` : "Customer (not a job)", h.street, h.linkedTo && `linked to ${h.linkedTo}`].filter(Boolean).join(" · ")}</div>
                </div>
                <Button className="px-3 py-1.5" disabled={!!h.linkedTo} busy={link.isPending && link.variables === h.id} onClick={() => link.mutate(h.id)}>
                  Link
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Dialog>
  );
}
